import { LitElement, html } from "../vendor/lit-element/lit-element";
import { gsap } from "gsap";
import { distributeByPosition } from "../utils/distributeByPosition";

class PhosphateDecreasesChart extends LitElement {
  async firstUpdated() {
    // Give the browser a chance to paint
    await new Promise((r) => setTimeout(r, 0));

    const tl = gsap
      .timeline()
      .fromTo(
        "#bubbles > *",
        {
          opacity: 0,
        },
        {
          opacity: 1,
          // This function courtesy GSAP, and it's basically magic.
          stagger: distributeByPosition({
            amount: 3, // the higher this is, the slower the animation.
            axis: "x", // which direction are we animating from
            from: 0, // _where_ we're animating from. This can also be things like "center"
          }),
        }
      )
      .pause(); // not yet.

    const options = {
      threshold: 0.5, // when half of this chart is visible, go ahead and start animating.
    };

    // Create our observer with the callback and options
    const observer = new IntersectionObserver((entries, observer) => {
      // This runs once on load, and once everytime the element intersects.
      entries.forEach((entry) => {
        // console.log(entry);
        // Therefore, we check is intersecting.
        if (entry.isIntersecting) {
          // Play the timeline if it's visible.
          tl.play();
          // And kill the observer, so it isn't calling this a billion times as someone scrolls.
          observer.unobserve(this);
        }
      });
    }, options);

    // Kick off the observer on this element. (`this` here is a reference to the component)
    observer.observe(this);
  }

  // This is needed to not append the render() result to a shadow dom (so that we can animate it.)
  createRenderRoot() {
    return this;
  }

  // Edits I made to this svg from the one in ./images/phosphate-decreases-chart:
  // - Added <g> wrapper around all the <use> elements, which are the bubbles in the chart.

  // I couldn't find a way to just.... import this that wasn't react specific.... so.... sorry.
  render() {
    return html`
      <svg
        xmlns="http://www.w3.org/2000/svg"
        xmlns:xlink="http://www.w3.org/1999/xlink"
        viewBox="0 0 355.5 199.33"
        id="phosphate-decreases-chart-root"
      >
        <defs>
          <symbol id="a" viewBox="0 0 17.72 17.72">
            <path
              d="M9.4,6H8.16A.12.12,0,0,0,8,6.09v2c0,.08.05.12.13.12H9.4a1.11,1.11,0,0,0,1.25-1.09A1.13,1.13,0,0,0,9.4,6Z"
              style="fill: #bfb8ff"
            />
            <path
              d="M8.86,0a8.86,8.86,0,1,0,8.86,8.86A8.86,8.86,0,0,0,8.86,0Zm.87,10.47H8.16a.12.12,0,0,0-.13.12v3.34a.26.26,0,0,1-.29.29H5.44a.26.26,0,0,1-.29-.29V3.79a.26.26,0,0,1,.29-.29H9.87A3.39,3.39,0,0,1,13.52,7C13.52,9.09,12,10.47,9.73,10.47Z"
              style="fill: #bfb8ff"
            />
          </symbol>
        </defs>
        <polyline
          points="44.9 0.5 44.9 158.01 355 158.01"
          style="
        fill: none;
        stroke: #6f6f6e;
        stroke-linecap: square;
        stroke-miterlimit: 10;
      "
        />
        <path
          d="M10.05,160H2.64a.22.22,0,0,1-.24-.24v-1.05a.22.22,0,0,1,.24-.24h7.44a2.4,2.4,0,0,0,0-4.79H2.64a.22.22,0,0,1-.24-.24v-1.05a.22.22,0,0,1,.24-.24h7.41a3.93,3.93,0,0,1,0,7.85Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M5.55,145.35a.24.24,0,0,1,.3-.12l1,.22c.18,0,.19.13.15.29a1.87,1.87,0,0,0-.07.78,1.72,1.72,0,0,0,1.8,1.73h4.59a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24v-1.06a.23.23,0,0,1,.24-.24h.74c.08,0,.09-.05,0-.08a2.24,2.24,0,0,1-1.09-1.95A2,2,0,0,1,5.55,145.35Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M3.23,144a1,1,0,0,1-1-1,1,1,0,0,1,1-1,1,1,0,0,1,1,1A1,1,0,0,1,3.23,144Zm10.13-.28H5.69a.23.23,0,0,1-.24-.24v-1.06a.23.23,0,0,1,.24-.24h7.67a.23.23,0,0,1,.24.24v1.06A.23.23,0,0,1,13.36,143.73Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M8.16,133.35h5.2a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H8.46a1.66,1.66,0,0,0-1.79,1.7,1.64,1.64,0,0,0,1.5,1.71h5.19a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24v-1.06a.23.23,0,0,1,.24-.24h.45c.06,0,.1-.05.05-.08a2.4,2.4,0,0,1-.85-2A2.64,2.64,0,0,1,8.16,133.35Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M8,125h5.33a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24h-.45c-.06,0-.09.05-.05.1a3.44,3.44,0,0,1,.87,2.4c0,1.44-.71,2.67-2.42,2.67s-2.67-1.2-2.67-3.31v-1.78a.07.07,0,0,0-.08-.08H8.14c-.91,0-1.49.51-1.49,1.65,0,.9.35,1.44.91,1.6a.24.24,0,0,1,.2.27l-.15,1.16c0,.14-.09.24-.2.22-1.21-.19-2.09-1.46-2.09-3.22C5.32,126.17,6.46,125,8,125Zm2.82,1.53H9.92a.07.07,0,0,0-.08.08v1.49c0,1.25.49,2.06,1.39,2.06s1.2-.67,1.2-1.52C12.43,127.5,11.79,126.49,10.85,126.49Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M5.55,118.46a.24.24,0,0,1,.3-.13l1,.23c.18,0,.19.12.15.28a1.92,1.92,0,0,0-.07.79,1.72,1.72,0,0,0,1.8,1.73h4.59a.23.23,0,0,1,.24.24v1.05a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24V121.6a.23.23,0,0,1,.24-.24h.74c.08,0,.09,0,0-.08a2.27,2.27,0,0,1-1.09-2A1.93,1.93,0,0,1,5.55,118.46Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M16.56,117.31h-.82c-.14,0-.24,0-.24-.1v-.09c0-1.24-.32-1.63-1.85-2.08,0,0-.05,0-.1,0l-7.83,2.64c-.16,0-.27,0-.27-.19v-1.12a.27.27,0,0,1,.19-.28l5.77-1.81c.08,0,.08-.08,0-.09l-5.75-1.75a.27.27,0,0,1-.21-.27v-1.12c0-.17.11-.24.27-.19l8.44,2.86c2.08.71,2.64,1.25,2.64,3.37v.11C16.8,117.26,16.71,117.31,16.56,117.31Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M9.55,99.24a6.78,6.78,0,0,1,1.94.24,3,3,0,0,1,2.24,3.08,2.41,2.41,0,0,1-.82,1.85c-.05,0,0,.1,0,.1h3.48a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.24h.42c.06,0,.09-.05,0-.1a2.36,2.36,0,0,1-.84-1.85,3,3,0,0,1,2.18-3A6.08,6.08,0,0,1,9.55,99.24Zm0,1.54a4.14,4.14,0,0,0-2,.4,1.63,1.63,0,0,0-.88,1.54,1.54,1.54,0,0,0,.87,1.39,4.29,4.29,0,0,0,2,.38,4.14,4.14,0,0,0,2.05-.41,1.51,1.51,0,0,0,.82-1.36,1.61,1.61,0,0,0-.77-1.43A3.81,3.81,0,0,0,9.52,100.78Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M8.16,90.83h5.2a.23.23,0,0,1,.24.24v1.05a.23.23,0,0,1-.24.24H8.46a1.66,1.66,0,0,0-1.79,1.7,1.64,1.64,0,0,0,1.5,1.71h5.19a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H2.64a.23.23,0,0,1-.24-.24V96a.23.23,0,0,1,.24-.24h3.5c.06,0,.1,0,.05-.08a2.4,2.4,0,0,1-.85-2A2.64,2.64,0,0,1,8.16,90.83Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M11.45,88.59a6.3,6.3,0,0,1-1.95.29,6.44,6.44,0,0,1-1.92-.27,3.31,3.31,0,0,1-2.26-3.33A3.27,3.27,0,0,1,7.56,82a6.16,6.16,0,0,1,1.93-.28,6.4,6.4,0,0,1,2,.28,3.49,3.49,0,0,1,0,6.55ZM11,83.48a6,6,0,0,0-1.5-.17A5.93,5.93,0,0,0,8,83.47a1.92,1.92,0,0,0,0,3.68,5.4,5.4,0,0,0,1.49.18,5.7,5.7,0,0,0,1.5-.18,1.91,1.91,0,0,0,0-3.67Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M11.5,80.09h-.09a.22.22,0,0,1-.24-.24v-1c0-.14,0-.24.11-.24h.13c.57,0,1.12-.7,1.12-1.71s-.48-1.63-1.11-1.63-.91.8-1.18,1.76c-.39,1.41-.88,3-2.53,3C6.25,80,5.37,78.78,5.37,77s1-3.13,2.31-3.13c.13,0,.21.1.21.25v1c0,.14,0,.24-.12.24H7.68c-.6,0-1.09.62-1.09,1.69s.37,1.59,1,1.59.93-.77,1.21-1.8c.37-1.37.84-3,2.52-3,1.45,0,2.37,1.25,2.37,3.15S12.72,80.09,11.5,80.09Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M9.55,65.05a6.78,6.78,0,0,1,1.94.24,3,3,0,0,1,2.24,3.07,2.42,2.42,0,0,1-.82,1.86c-.05,0,0,.1,0,.1h3.48a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.24h.42c.06,0,.09,0,0-.1a2.37,2.37,0,0,1-.84-1.86,3,3,0,0,1,2.18-3A6.08,6.08,0,0,1,9.55,65.05Zm0,1.54a4.14,4.14,0,0,0-2,.4,1.63,1.63,0,0,0-.88,1.53,1.54,1.54,0,0,0,.87,1.4,4.29,4.29,0,0,0,2,.38,4,4,0,0,0,2.05-.42,1.48,1.48,0,0,0,.82-1.36,1.6,1.6,0,0,0-.77-1.42A3.81,3.81,0,0,0,9.52,66.59Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M8.16,56.64h5.2a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H8.46a1.66,1.66,0,0,0-1.79,1.7,1.64,1.64,0,0,0,1.5,1.71h5.19a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H2.64a.23.23,0,0,1-.24-.24V61.82a.23.23,0,0,1,.24-.24h3.5c.06,0,.1,0,.05-.08a2.4,2.4,0,0,1-.85-2A2.64,2.64,0,0,1,8.16,56.64Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M8,48.24h5.33a.23.23,0,0,1,.24.24v1.05a.23.23,0,0,1-.24.24h-.45c-.06,0-.09,0-.05.1a3.44,3.44,0,0,1,.87,2.4c0,1.44-.71,2.67-2.42,2.67s-2.67-1.2-2.67-3.31V49.85a.07.07,0,0,0-.08-.08H8.14c-.91,0-1.49.52-1.49,1.65,0,.9.35,1.44.91,1.6a.24.24,0,0,1,.2.27l-.15,1.16c0,.14-.09.24-.2.22-1.21-.19-2.09-1.46-2.09-3.22C5.32,49.45,6.46,48.24,8,48.24Zm2.82,1.53H9.92a.07.07,0,0,0-.08.08v1.49c0,1.25.49,2.07,1.39,2.07s1.2-.68,1.2-1.52C12.43,50.78,11.79,49.77,10.85,49.77Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M6.72,42.4V44a.07.07,0,0,0,.08.08H11c.93,0,1.27-.4,1.27-1.25v-.38a.22.22,0,0,1,.24-.24h.83a.24.24,0,0,1,.26.24c0,.22,0,.43,0,.65,0,1.49-.36,2.45-2.05,2.45H6.8a.07.07,0,0,0-.08.08v.88a.23.23,0,0,1-.24.24H5.69a.23.23,0,0,1-.24-.24v-.88a.07.07,0,0,0-.08-.08H3.58a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.24H5.37A.07.07,0,0,0,5.45,44V42.4a.23.23,0,0,1,.24-.24h.79A.23.23,0,0,1,6.72,42.4Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M11.41,35c-.15-.08-.2-.18-.1-.32l.4-.66a.23.23,0,0,1,.34-.09A3.33,3.33,0,0,1,13.73,37a3.11,3.11,0,0,1-2,3.1,6,6,0,0,1-2.28.41,6.5,6.5,0,0,1-1.78-.18,3.17,3.17,0,0,1-2.39-3.25c0-2.1,1.17-3.06,3.09-3.36a10.3,10.3,0,0,1,1.44-.11.22.22,0,0,1,.24.24v5a.08.08,0,0,0,.08.08,3,3,0,0,0,.87-.11,1.93,1.93,0,0,0,1.38-2A2.05,2.05,0,0,0,11.41,35ZM7.92,38.81a3.5,3.5,0,0,0,.89.15.07.07,0,0,0,.08-.08V35.25a.07.07,0,0,0-.08-.08,3.63,3.63,0,0,0-.78.09,1.77,1.77,0,0,0-1.38,1.81A1.66,1.66,0,0,0,7.92,38.81Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M28.41,154.76c-.15-.08-.2-.17-.1-.32l.4-.65a.24.24,0,0,1,.34-.1,3.34,3.34,0,0,1,1.68,3.07,3.13,3.13,0,0,1-2,3.11,6.16,6.16,0,0,1-2.28.4,6.13,6.13,0,0,1-1.78-.18,3.16,3.16,0,0,1-2.39-3.25c0-2.09,1.17-3.05,3.09-3.36.44-.06.9-.09,1.44-.11a.22.22,0,0,1,.24.24v5.06a.08.08,0,0,0,.08.08,3.86,3.86,0,0,0,.87-.11,1.94,1.94,0,0,0,1.38-2A2.05,2.05,0,0,0,28.41,154.76Zm-3.49,3.83a3.13,3.13,0,0,0,.89.14.07.07,0,0,0,.08-.08V155a.07.07,0,0,0-.08-.08A4.42,4.42,0,0,0,25,155a1.77,1.77,0,0,0-1.38,1.81A1.68,1.68,0,0,0,24.92,158.59Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M30.31,152.05l-3.73-2.58a.08.08,0,0,0-.09,0l-3.75,2.58c-.18.11-.29,0-.29-.15v-1.12a.36.36,0,0,1,.16-.3l2.66-1.81a.06.06,0,0,0,0-.11l-2.66-1.81a.4.4,0,0,1-.16-.31v-1.21c0-.2.11-.26.29-.15l3.75,2.58a.08.08,0,0,0,.09,0l3.73-2.58c.18-.11.29-.05.29.15v1.13a.37.37,0,0,1-.16.31l-2.66,1.81a.06.06,0,0,0,0,.11l2.66,1.81a.34.34,0,0,1,.16.3v1.2C30.6,152.09,30.49,152.16,30.31,152.05Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M28.44,143.45a6.2,6.2,0,0,1-1.94.24,6.52,6.52,0,0,1-1.94-.24,3.23,3.23,0,0,1-2.24-3.26,3.28,3.28,0,0,1,2.15-3.27,2.25,2.25,0,0,1,.45-.11.21.21,0,0,1,.25.21l.15,1a.23.23,0,0,1-.18.27l-.21,0a1.8,1.8,0,0,0-1.28,1.82A1.75,1.75,0,0,0,25,142a6.78,6.78,0,0,0,1.52.14A6.38,6.38,0,0,0,28,142a1.75,1.75,0,0,0,1.38-1.8,1.81,1.81,0,0,0-1.28-1.82.14.14,0,0,0-.07,0h0a.27.27,0,0,1-.21-.27l.18-1a.23.23,0,0,1,.25-.21l.31.08a3.51,3.51,0,0,1-.13,6.53Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M22.55,130.6a.24.24,0,0,1,.3-.13l1,.23c.18,0,.19.13.15.29a1.87,1.87,0,0,0-.07.78,1.72,1.72,0,0,0,1.8,1.73h4.59a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H22.69a.23.23,0,0,1-.24-.24v-1.06a.23.23,0,0,1,.24-.24h.74c.08,0,.09-.05,0-.08a2.24,2.24,0,0,1-1.09-1.95A2,2,0,0,1,22.55,130.6Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M28.41,123.82c-.15-.08-.2-.18-.1-.32l.4-.66a.23.23,0,0,1,.34-.09,3.33,3.33,0,0,1,1.68,3.07,3.11,3.11,0,0,1-2,3.1,6,6,0,0,1-2.28.41,6.5,6.5,0,0,1-1.78-.18,3.17,3.17,0,0,1-2.39-3.25c0-2.1,1.17-3.06,3.09-3.36a10.3,10.3,0,0,1,1.44-.11.22.22,0,0,1,.24.24v5a.08.08,0,0,0,.08.08,3,3,0,0,0,.87-.11,1.93,1.93,0,0,0,1.38-2A2.05,2.05,0,0,0,28.41,123.82Zm-3.49,3.82a3.5,3.5,0,0,0,.89.15.07.07,0,0,0,.08-.08v-3.63a.07.07,0,0,0-.08-.08,3.63,3.63,0,0,0-.78.09,1.77,1.77,0,0,0-1.38,1.81A1.66,1.66,0,0,0,24.92,127.64Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M23.72,116.72v1.63a.07.07,0,0,0,.08.08H28c.93,0,1.27-.4,1.27-1.25v-.38a.22.22,0,0,1,.24-.24h.83a.24.24,0,0,1,.26.24c0,.22,0,.43,0,.65,0,1.49-.36,2.45-2,2.45H23.8a.07.07,0,0,0-.08.08v.88a.23.23,0,0,1-.24.24h-.79a.23.23,0,0,1-.24-.24V120a.07.07,0,0,0-.08-.08H20.58a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.24h1.79a.07.07,0,0,0,.08-.08v-1.63a.23.23,0,0,1,.24-.24h.79A.23.23,0,0,1,23.72,116.72Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M20.23,114.75a1,1,0,0,1-1-1.06,1,1,0,1,1,2,0A1,1,0,0,1,20.23,114.75Zm10.13-.29H22.69a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.25h7.67a.23.23,0,0,1,.24.25v1A.23.23,0,0,1,30.36,114.46Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M28.45,110.53a6.64,6.64,0,0,1-1.95.28,6.14,6.14,0,0,1-1.92-.27,3.52,3.52,0,0,1,0-6.56,6.16,6.16,0,0,1,1.93-.29,6.4,6.4,0,0,1,2,.29,3.49,3.49,0,0,1,0,6.55ZM28,105.42a5.92,5.92,0,0,0-1.5-.18,6.44,6.44,0,0,0-1.49.16,1.88,1.88,0,0,0-1.38,1.86A1.85,1.85,0,0,0,25,109.08a5,5,0,0,0,1.49.18,5.3,5.3,0,0,0,1.5-.18,1.9,1.9,0,0,0,0-3.66Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M25.16,95.21h5.2a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24h-4.9a1.71,1.71,0,0,0-.29,3.41h5.19a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H22.69a.23.23,0,0,1-.24-.24v-1a.23.23,0,0,1,.24-.24h.45c.06,0,.1,0,.05-.08a2.43,2.43,0,0,1-.85-2A2.64,2.64,0,0,1,25.16,95.21Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M32.06,86a12.35,12.35,0,0,1-6.71,1.81A12.34,12.34,0,0,1,18.55,86a.34.34,0,0,1-.15-.29v-1c0-.19.11-.27.29-.18a14.15,14.15,0,0,0,6.64,1.57,13.85,13.85,0,0,0,6.58-1.57c.18-.09.29,0,.29.18v1A.37.37,0,0,1,32.06,86Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M25.09,70.87h5.27a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H25.41A1.58,1.58,0,0,0,23.67,74a1.6,1.6,0,0,0,1.71,1.67h5a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24H25.41a1.62,1.62,0,1,0,0,3.23h5a.23.23,0,0,1,.24.24v1.06a.23.23,0,0,1-.24.24H22.69a.23.23,0,0,1-.24-.24V80.67a.23.23,0,0,1,.24-.24h.43c.07,0,.1-.05.05-.1a2.58,2.58,0,0,1-.83-2A2.43,2.43,0,0,1,23.56,76s0-.06,0-.11a2.63,2.63,0,0,1-1.22-2.4A2.48,2.48,0,0,1,25.09,70.87Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M22.45,63.5V62.44a.23.23,0,0,1,.24-.24h7.49c3,0,3.76,2.23,3.57,4.76,0,.16-.11.22-.25.22l-1,0a.22.22,0,0,1-.22-.27c.14-2.2-.4-3.14-2.19-3.14h-.28c-.06,0-.08,0,0,.1a2.45,2.45,0,0,1,.77,2,3,3,0,0,1-2.15,3A7.1,7.1,0,0,1,26.5,69a6.71,6.71,0,0,1-2.05-.24,3,3,0,0,1-2.13-2.91,2.65,2.65,0,0,1,.79-2c.06-.05,0-.1,0-.1h-.39A.23.23,0,0,1,22.45,63.5Zm4,.24a5.3,5.3,0,0,0-1.49.11,1.67,1.67,0,0,0-1.35,1.7A1.8,1.8,0,0,0,25,67.31a4.38,4.38,0,0,0,1.49.18,4.84,4.84,0,0,0,1.5-.16,1.79,1.79,0,0,0,1.34-1.78A1.65,1.65,0,0,0,28,63.84,5.89,5.89,0,0,0,26.49,63.74Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M30.33,60.69,19.59,57a.25.25,0,0,1-.19-.27v-1c0-.18.11-.24.27-.19l10.74,3.71a.26.26,0,0,1,.19.27v1C30.6,60.67,30.49,60.73,30.33,60.69Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M19.4,48.65v-1a.22.22,0,0,1,.24-.24H30.36a.23.23,0,0,1,.24.24v1a.23.23,0,0,1-.24.24h-.42c-.06,0-.09,0,0,.1a2.39,2.39,0,0,1,.83,1.86,3,3,0,0,1-2.18,3,6,6,0,0,1-2.05.29,6.49,6.49,0,0,1-1.94-.24,3,3,0,0,1-2.24-3.07A2.42,2.42,0,0,1,23.14,49c.05-.05,0-.1,0-.1H19.64A.22.22,0,0,1,19.4,48.65Zm7.12.26a4,4,0,0,0-2.05.42,1.48,1.48,0,0,0-.82,1.36,1.6,1.6,0,0,0,.77,1.42,3.81,3.81,0,0,0,2.11.51,4.14,4.14,0,0,0,2-.4,1.61,1.61,0,0,0,.88-1.53,1.54,1.54,0,0,0-.87-1.4A4.29,4.29,0,0,0,26.52,48.91Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M30.36,44.91H19.64a.22.22,0,0,1-.24-.24V43.61a.23.23,0,0,1,.24-.24h9.55a.07.07,0,0,0,.08-.08V37.87a.23.23,0,0,1,.24-.24h.85a.23.23,0,0,1,.24.24v6.8A.23.23,0,0,1,30.36,44.91Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M32.2,34.51v1c0,.19-.11.27-.29.18a13.85,13.85,0,0,0-6.58-1.57,14.15,14.15,0,0,0-6.64,1.57c-.18.09-.29,0-.29-.18v-1a.34.34,0,0,1,.15-.29,12.34,12.34,0,0,1,6.8-1.84,12.35,12.35,0,0,1,6.71,1.81A.37.37,0,0,1,32.2,34.51Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <line
          x1="81.65"
          y1="22.52"
          x2="81.65"
          y2="34.63"
          style="
        fill: none;
        stroke: #4d21b5;
        stroke-linecap: round;
        stroke-miterlimit: 8;
        stroke-dasharray: 0.10000000149011612, 3;
      "
        />
        <line
          x1="162.23"
          y1="22.52"
          x2="162.23"
          y2="68.38"
          style="
        fill: none;
        stroke: #4d21b5;
        stroke-linecap: round;
        stroke-miterlimit: 8;
        stroke-dasharray: 0.10000000149011612, 3;
      "
        />
        <line
          x1="242.8"
          y1="22.52"
          x2="242.8"
          y2="99.88"
          style="
        fill: none;
        stroke: #4d21b5;
        stroke-linecap: round;
        stroke-miterlimit: 8;
        stroke-dasharray: 0.10000000149011612, 3;
      "
        />
        <path
          d="M64.23,181.12v-.38a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v.3c0,1.06,1,1.87,2.57,1.87s2.23-.72,2.23-1.68-.86-1.45-2.74-2.21-3.42-1.34-3.42-3.29,1.42-2.95,3.65-2.95c2.4,0,4,1.28,4,3.19v.24a.23.23,0,0,1-.24.24h-1a.21.21,0,0,1-.24-.21v-.19c0-1.08-.88-1.94-2.5-1.94-1.33,0-2.08.56-2.08,1.58s.83,1.43,2.72,2.15,3.48,1.39,3.48,3.34-1.46,3.06-3.88,3.06S64.23,183,64.23,181.12Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M77.74,177.23H76.11a.07.07,0,0,0-.08.08v4.18c0,.93.4,1.26,1.24,1.26h.39a.23.23,0,0,1,.24.24v.84a.23.23,0,0,1-.24.25l-.66,0c-1.49,0-2.45-.37-2.45-2v-4.74a.07.07,0,0,0-.08-.08h-.88a.23.23,0,0,1-.24-.24v-.78a.23.23,0,0,1,.24-.24h.88a.08.08,0,0,0,.08-.08v-1.8a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v1.8a.08.08,0,0,0,.08.08h1.63a.23.23,0,0,1,.24.24V177A.23.23,0,0,1,77.74,177.23Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M86.08,178.54v5.33a.23.23,0,0,1-.24.24H84.78a.23.23,0,0,1-.24-.24v-.45c0-.06-.05-.09-.1,0a3.44,3.44,0,0,1-2.4.86c-1.44,0-2.67-.7-2.67-2.42s1.2-2.67,3.31-2.67h1.78a.07.07,0,0,0,.08-.08v-.41c0-.92-.51-1.49-1.65-1.49-.9,0-1.44.35-1.6.91-.05.14-.13.21-.27.19l-1.15-.14c-.15,0-.25-.1-.23-.21.19-1.2,1.46-2.08,3.22-2.08C84.86,175.84,86.08,177,86.08,178.54Zm-1.54,2.82v-.93a.07.07,0,0,0-.08-.08H83c-1.25,0-2.06.5-2.06,1.39s.67,1.2,1.52,1.2C83.53,182.94,84.54,182.3,84.54,181.36Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M93.39,176h1a.22.22,0,0,1,.24.24v7.49c0,3-2.22,3.76-4.75,3.57-.16,0-.22-.12-.22-.26l0-.94a.23.23,0,0,1,.27-.23c2.19.15,3.14-.4,3.14-2.19v-.27c0-.07-.05-.08-.1,0a2.47,2.47,0,0,1-2,.77,3,3,0,0,1-3-2.14,7.2,7.2,0,0,1-.21-1.95,6.63,6.63,0,0,1,.24-2.05A3,3,0,0,1,91,175.84a2.69,2.69,0,0,1,2,.78c0,.07.1,0,.1,0v-.38A.22.22,0,0,1,93.39,176Zm-.24,4a5.14,5.14,0,0,0-.12-1.49,1.65,1.65,0,0,0-1.69-1.34,1.8,1.8,0,0,0-1.76,1.34A4.46,4.46,0,0,0,89.4,180a4.84,4.84,0,0,0,.16,1.5,1.79,1.79,0,0,0,1.78,1.35,1.66,1.66,0,0,0,1.71-1.35A6.38,6.38,0,0,0,93.15,180Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M102.11,181.92c.08-.14.17-.19.32-.1l.65.4a.25.25,0,0,1,.1.34,3.34,3.34,0,0,1-3.07,1.68,3.14,3.14,0,0,1-3.11-2,6.25,6.25,0,0,1-.4-2.29,6.13,6.13,0,0,1,.18-1.78,3.16,3.16,0,0,1,3.25-2.38c2.09,0,3,1.17,3.36,3.09a14.14,14.14,0,0,1,.11,1.44.22.22,0,0,1-.24.24H98.2a.07.07,0,0,0-.08.08,3.84,3.84,0,0,0,.11.86,1.94,1.94,0,0,0,2,1.38A2.07,2.07,0,0,0,102.11,181.92Zm-3.83-3.49a3.21,3.21,0,0,0-.14.9.07.07,0,0,0,.08.08h3.63a.07.07,0,0,0,.08-.08,3.64,3.64,0,0,0-.1-.79,1.75,1.75,0,0,0-1.8-1.37A1.67,1.67,0,0,0,98.28,178.43Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M76.41,189.91h1.14a.23.23,0,0,1,.24.24v10.72a.23.23,0,0,1-.24.24H76.49a.23.23,0,0,1-.24-.24v-9.38a.07.07,0,0,0-.1-.06l-1.58.54c-.16.07-.27,0-.27-.19V191a.31.31,0,0,1,.17-.27l1.64-.8A.6.6,0,0,1,76.41,189.91Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M79.62,196.65v-.85a.23.23,0,0,1,.24-.24h4.79a.23.23,0,0,1,.24.24v.85a.23.23,0,0,1-.24.24H79.86A.23.23,0,0,1,79.62,196.65Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M86.33,200.87V200a.43.43,0,0,1,.11-.3c1.27-1.43,2.45-2.87,3.57-4.21a5.22,5.22,0,0,0,1.49-2.76,1.66,1.66,0,0,0-1.84-1.66,1.63,1.63,0,0,0-1.81,1.68v.4a.23.23,0,0,1-.24.24H86.54a.23.23,0,0,1-.24-.24v-.61c.06-1.7,1.45-2.8,3.36-2.8,2.08,0,3.39,1.28,3.39,3a5.34,5.34,0,0,1-1.44,3.14c-1,1.23-2,2.48-3.2,3.81,0,.05,0,.1,0,.1h4.66a.22.22,0,0,1,.24.23v.85a.23.23,0,0,1-.24.24H86.57A.22.22,0,0,1,86.33,200.87Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M144.81,181.12v-.38a.22.22,0,0,1,.24-.24h1a.22.22,0,0,1,.24.24v.3c0,1.06,1,1.87,2.58,1.87s2.23-.72,2.23-1.68-.87-1.45-2.74-2.21-3.43-1.34-3.43-3.29,1.43-2.95,3.65-2.95c2.4,0,4,1.28,4,3.19v.24a.23.23,0,0,1-.24.24h-1c-.15,0-.24-.08-.24-.21v-.19c0-1.08-.89-1.94-2.5-1.94-1.33,0-2.08.56-2.08,1.58s.83,1.43,2.72,2.15,3.47,1.39,3.47,3.34-1.45,3.06-3.87,3.06S144.81,183,144.81,181.12Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M158.31,177.23h-1.63a.07.07,0,0,0-.08.08v4.18c0,.93.4,1.26,1.25,1.26h.38a.23.23,0,0,1,.24.24v.84a.23.23,0,0,1-.24.25l-.65,0c-1.49,0-2.45-.37-2.45-2v-4.74a.07.07,0,0,0-.08-.08h-.88a.23.23,0,0,1-.24-.24v-.78a.22.22,0,0,1,.24-.24h.88a.08.08,0,0,0,.08-.08v-1.8a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v1.8a.08.08,0,0,0,.08.08h1.63a.22.22,0,0,1,.24.24V177A.23.23,0,0,1,158.31,177.23Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M166.65,178.54v5.33a.22.22,0,0,1-.24.24h-1.06a.23.23,0,0,1-.24-.24v-.45c0-.06,0-.09-.09,0a3.44,3.44,0,0,1-2.4.86c-1.44,0-2.68-.7-2.68-2.42s1.21-2.67,3.32-2.67H165a.07.07,0,0,0,.08-.08v-.41c0-.92-.51-1.49-1.64-1.49-.9,0-1.44.35-1.6.91a.24.24,0,0,1-.28.19l-1.15-.14c-.14,0-.24-.1-.22-.21.19-1.2,1.45-2.08,3.21-2.08C165.43,175.84,166.65,177,166.65,178.54Zm-1.54,2.82v-.93a.07.07,0,0,0-.08-.08h-1.48c-1.25,0-2.07.5-2.07,1.39s.67,1.2,1.52,1.2C164.11,182.94,165.11,182.3,165.11,181.36Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M174,176H175a.23.23,0,0,1,.24.24v7.49c0,3-2.23,3.76-4.76,3.57-.16,0-.22-.12-.22-.26l0-.94a.23.23,0,0,1,.27-.23c2.2.15,3.14-.4,3.14-2.19v-.27c0-.07-.05-.08-.09,0a2.48,2.48,0,0,1-2,.77,3,3,0,0,1-3-2.14,7.2,7.2,0,0,1-.21-1.95,6.63,6.63,0,0,1,.24-2.05,3,3,0,0,1,2.92-2.13,2.71,2.71,0,0,1,2,.78c0,.07.09,0,.09,0v-.38A.23.23,0,0,1,174,176Zm-.24,4a5.3,5.3,0,0,0-.11-1.49,1.66,1.66,0,0,0-1.7-1.34,1.81,1.81,0,0,0-1.76,1.34,6.85,6.85,0,0,0,0,3,1.78,1.78,0,0,0,1.77,1.35,1.66,1.66,0,0,0,1.72-1.35A6.65,6.65,0,0,0,173.72,180Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M182.68,181.92c.08-.14.18-.19.32-.1l.66.4a.25.25,0,0,1,.1.34,3.35,3.35,0,0,1-3.08,1.68,3.12,3.12,0,0,1-3.1-2,6.05,6.05,0,0,1-.4-2.29,6.54,6.54,0,0,1,.17-1.78,3.17,3.17,0,0,1,3.25-2.38c2.1,0,3.06,1.17,3.36,3.09a11.61,11.61,0,0,1,.12,1.44.22.22,0,0,1-.24.24h-5.06a.07.07,0,0,0-.08.08,3.33,3.33,0,0,0,.11.86,1.93,1.93,0,0,0,2,1.38A2,2,0,0,0,182.68,181.92Zm-3.82-3.49a3.58,3.58,0,0,0-.15.9.08.08,0,0,0,.08.08h3.64a.08.08,0,0,0,.08-.08,4.4,4.4,0,0,0-.1-.79,1.77,1.77,0,0,0-1.81-1.37A1.66,1.66,0,0,0,178.86,178.43Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M167.72,197.61a5.11,5.11,0,0,1-.25,1.57,3.11,3.11,0,0,1-3.14,2.06,3.18,3.18,0,0,1-3.17-2.21,4.44,4.44,0,0,1-.21-1.18.22.22,0,0,1,.24-.24h1.07a.22.22,0,0,1,.24.24,7.7,7.7,0,0,0,.13.77,1.68,1.68,0,0,0,1.7,1.28,1.59,1.59,0,0,0,1.62-1.08,3.51,3.51,0,0,0,.22-1.24,3.56,3.56,0,0,0-.27-1.46,1.59,1.59,0,0,0-1.59-1,1.18,1.18,0,0,0-.56.15.24.24,0,0,1-.33-.07l-.51-.62a.24.24,0,0,1,0-.34l2.69-2.86c0-.06,0-.1,0-.1h-4.24a.22.22,0,0,1-.24-.24v-.86a.22.22,0,0,1,.24-.24h6a.23.23,0,0,1,.24.24v.93a.41.41,0,0,1-.11.3l-2.45,2.67s0,.1.06.12a2.53,2.53,0,0,1,2.27,1.63A4.62,4.62,0,0,1,167.72,197.61Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M225.38,181.12v-.38a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v.3c0,1.06,1,1.87,2.58,1.87s2.22-.72,2.22-1.68-.86-1.45-2.74-2.21-3.42-1.34-3.42-3.29,1.42-2.95,3.65-2.95c2.4,0,3.95,1.28,3.95,3.19v.24a.23.23,0,0,1-.24.24h-1a.21.21,0,0,1-.24-.21v-.19c0-1.08-.88-1.94-2.5-1.94-1.33,0-2.08.56-2.08,1.58s.83,1.43,2.72,2.15,3.48,1.39,3.48,3.34-1.46,3.06-3.88,3.06S225.38,183,225.38,181.12Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M238.89,177.23h-1.63a.07.07,0,0,0-.08.08v4.18c0,.93.4,1.26,1.24,1.26h.39a.23.23,0,0,1,.24.24v.84a.23.23,0,0,1-.24.25l-.66,0c-1.49,0-2.45-.37-2.45-2v-4.74a.07.07,0,0,0-.08-.08h-.88a.23.23,0,0,1-.24-.24v-.78a.23.23,0,0,1,.24-.24h.88a.08.08,0,0,0,.08-.08v-1.8a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v1.8a.08.08,0,0,0,.08.08h1.63a.23.23,0,0,1,.24.24V177A.23.23,0,0,1,238.89,177.23Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M247.23,178.54v5.33a.23.23,0,0,1-.24.24h-1.06a.23.23,0,0,1-.24-.24v-.45c0-.06-.05-.09-.1,0a3.44,3.44,0,0,1-2.4.86c-1.44,0-2.67-.7-2.67-2.42s1.2-2.67,3.31-2.67h1.78a.07.07,0,0,0,.08-.08v-.41c0-.92-.51-1.49-1.65-1.49-.89,0-1.44.35-1.6.91-.05.14-.13.21-.27.19l-1.15-.14c-.15,0-.24-.1-.23-.21.19-1.2,1.46-2.08,3.22-2.08C246,175.84,247.23,177,247.23,178.54Zm-1.54,2.82v-.93a.07.07,0,0,0-.08-.08h-1.49c-1.25,0-2.06.5-2.06,1.39s.67,1.2,1.52,1.2C244.68,182.94,245.69,182.3,245.69,181.36Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M254.54,176h1.05a.22.22,0,0,1,.24.24v7.49c0,3-2.22,3.76-4.75,3.57-.16,0-.22-.12-.22-.26l0-.94a.23.23,0,0,1,.27-.23c2.19.15,3.14-.4,3.14-2.19v-.27c0-.07-.05-.08-.1,0a2.45,2.45,0,0,1-1.95.77,3,3,0,0,1-3-2.14A7.2,7.2,0,0,1,249,180a6.63,6.63,0,0,1,.24-2.05,3,3,0,0,1,2.91-2.13,2.69,2.69,0,0,1,2,.78c.05.07.1,0,.1,0v-.38A.22.22,0,0,1,254.54,176Zm-.24,4a5.3,5.3,0,0,0-.11-1.49,1.66,1.66,0,0,0-1.7-1.34,1.8,1.8,0,0,0-1.76,1.34,4.46,4.46,0,0,0-.18,1.49,4.84,4.84,0,0,0,.16,1.5,1.79,1.79,0,0,0,1.78,1.35,1.65,1.65,0,0,0,1.71-1.35A5.83,5.83,0,0,0,254.3,180Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M263.26,181.92c.08-.14.17-.19.32-.1l.65.4a.25.25,0,0,1,.1.34,3.34,3.34,0,0,1-3.07,1.68,3.14,3.14,0,0,1-3.11-2,6.25,6.25,0,0,1-.4-2.29,6.13,6.13,0,0,1,.18-1.78,3.16,3.16,0,0,1,3.25-2.38c2.09,0,3.05,1.17,3.36,3.09a14.14,14.14,0,0,1,.11,1.44.22.22,0,0,1-.24.24h-5.06a.07.07,0,0,0-.08.08,3.84,3.84,0,0,0,.11.86,1.94,1.94,0,0,0,2,1.38A2.05,2.05,0,0,0,263.26,181.92Zm-3.83-3.49a3.21,3.21,0,0,0-.14.9.07.07,0,0,0,.08.08H263a.07.07,0,0,0,.08-.08,4.55,4.55,0,0,0-.09-.79,1.77,1.77,0,0,0-1.81-1.37A1.67,1.67,0,0,0,259.43,178.43Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M248.7,197v.89a.23.23,0,0,1-.24.24h-.61a.08.08,0,0,0-.08.08v2.69a.23.23,0,0,1-.24.24h-1.06a.23.23,0,0,1-.24-.24v-2.69a.08.08,0,0,0-.08-.08h-4.77a.23.23,0,0,1-.24-.24v-.72a.66.66,0,0,1,.07-.3l3.1-6.76a.29.29,0,0,1,.28-.17h1.12c.17,0,.25.11.17.29l-2.94,6.43c0,.05,0,.1,0,.1h3.17a.07.07,0,0,0,.08-.08v-2.29a.23.23,0,0,1,.24-.24h1.06a.23.23,0,0,1,.24.24v2.29a.07.07,0,0,0,.08.08h.61A.23.23,0,0,1,248.7,197Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M306,181.12v-.38a.23.23,0,0,1,.24-.24h1a.22.22,0,0,1,.24.24v.3c0,1.06,1,1.87,2.58,1.87s2.23-.72,2.23-1.68-.87-1.45-2.74-2.21-3.43-1.34-3.43-3.29,1.43-2.95,3.65-2.95c2.4,0,4,1.28,4,3.19v.24a.23.23,0,0,1-.24.24h-1c-.15,0-.24-.08-.24-.21v-.19c0-1.08-.88-1.94-2.5-1.94-1.33,0-2.08.56-2.08,1.58s.83,1.43,2.72,2.15,3.47,1.39,3.47,3.34-1.45,3.06-3.87,3.06S306,183,306,181.12Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M319.47,177.23h-1.64a.07.07,0,0,0-.08.08v4.18c0,.93.4,1.26,1.25,1.26h.39a.23.23,0,0,1,.24.24v.84a.23.23,0,0,1-.24.25l-.66,0c-1.49,0-2.45-.37-2.45-2v-4.74a.07.07,0,0,0-.08-.08h-.88a.23.23,0,0,1-.24-.24v-.78a.23.23,0,0,1,.24-.24h.88a.08.08,0,0,0,.08-.08v-1.8a.23.23,0,0,1,.24-.24h1a.23.23,0,0,1,.24.24v1.8a.08.08,0,0,0,.08.08h1.64a.23.23,0,0,1,.24.24V177A.23.23,0,0,1,319.47,177.23Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M327.8,178.54v5.33a.22.22,0,0,1-.24.24h-1.05a.22.22,0,0,1-.24-.24v-.45c0-.06,0-.09-.1,0a3.44,3.44,0,0,1-2.4.86c-1.44,0-2.67-.7-2.67-2.42s1.2-2.67,3.31-2.67h1.78a.08.08,0,0,0,.08-.08v-.41c0-.92-.52-1.49-1.65-1.49-.9,0-1.44.35-1.6.91a.24.24,0,0,1-.28.19l-1.15-.14c-.14,0-.24-.1-.22-.21.19-1.2,1.45-2.08,3.22-2.08C326.59,175.84,327.8,177,327.8,178.54Zm-1.53,2.82v-.93a.08.08,0,0,0-.08-.08H324.7c-1.25,0-2.07.5-2.07,1.39s.67,1.2,1.52,1.2C325.26,182.94,326.27,182.3,326.27,181.36Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M335.11,176h1.06a.23.23,0,0,1,.24.24v7.49c0,3-2.22,3.76-4.75,3.57a.23.23,0,0,1-.23-.26l0-.94a.24.24,0,0,1,.28-.23c2.19.15,3.13-.4,3.13-2.19v-.27c0-.07,0-.08-.09,0a2.48,2.48,0,0,1-2,.77,3,3,0,0,1-3-2.14,7.22,7.22,0,0,1-.2-1.95,6.34,6.34,0,0,1,.24-2.05,3,3,0,0,1,2.91-2.13,2.71,2.71,0,0,1,2,.78c.05.07.09,0,.09,0v-.38A.23.23,0,0,1,335.11,176Zm-.24,4a5.3,5.3,0,0,0-.11-1.49,1.66,1.66,0,0,0-1.7-1.34,1.81,1.81,0,0,0-1.76,1.34,6.85,6.85,0,0,0,0,3,1.79,1.79,0,0,0,1.77,1.35,1.66,1.66,0,0,0,1.72-1.35A6.65,6.65,0,0,0,334.87,180Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M343.83,181.92c.08-.14.18-.19.32-.1l.66.4a.25.25,0,0,1,.1.34,3.35,3.35,0,0,1-3.08,1.68,3.13,3.13,0,0,1-3.1-2,6.05,6.05,0,0,1-.4-2.29,6.54,6.54,0,0,1,.17-1.78,3.17,3.17,0,0,1,3.25-2.38c2.1,0,3.06,1.17,3.36,3.09a11.61,11.61,0,0,1,.12,1.44.22.22,0,0,1-.24.24h-5.06a.07.07,0,0,0-.08.08,3.33,3.33,0,0,0,.11.86,1.93,1.93,0,0,0,2,1.38A2,2,0,0,0,343.83,181.92ZM340,178.43a3.58,3.58,0,0,0-.15.9.08.08,0,0,0,.08.08h3.64a.07.07,0,0,0,.08-.08,4.4,4.4,0,0,0-.1-.79,1.77,1.77,0,0,0-1.81-1.37A1.66,1.66,0,0,0,340,178.43Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M316.24,197.61a4.89,4.89,0,0,1-.19,1.39,3.41,3.41,0,0,1-6.37.08,4.69,4.69,0,0,1-.18-.69.22.22,0,0,1,.24-.25h1.09a.24.24,0,0,1,.26.22,1.22,1.22,0,0,0,.06.26,1.76,1.76,0,0,0,3.4-.08,2.76,2.76,0,0,0,.12-.9,3.84,3.84,0,0,0-.09-1,1.59,1.59,0,0,0-1.73-1.26,1.74,1.74,0,0,0-1.68.91.31.31,0,0,1-.29.19h-1.14a.23.23,0,0,1-.24-.24v-6.08a.23.23,0,0,1,.24-.24h5.83a.23.23,0,0,1,.24.24V191a.23.23,0,0,1-.24.24h-4.45c-.05,0-.08,0-.08.08l0,3.31c0,.08.05.08.1.05a3,3,0,0,1,1.94-.66,2.92,2.92,0,0,1,3,2.13A6.11,6.11,0,0,1,316.24,197.61Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M317.89,202.23l.62-3.39a.24.24,0,0,1,.26-.22h.89c.16,0,.26.09.21.27l-.91,3.39a.24.24,0,0,1-.26.21h-.59C318,202.49,317.86,202.41,317.89,202.23Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M332,197.61a4.56,4.56,0,0,1-.19,1.39,3.41,3.41,0,0,1-6.37.08,3.65,3.65,0,0,1-.17-.69c0-.16.08-.25.24-.25h1.08a.24.24,0,0,1,.26.22,2.44,2.44,0,0,0,.06.26,1.76,1.76,0,0,0,3.4-.08,2.79,2.79,0,0,0,.13-.9,4.39,4.39,0,0,0-.1-1,1.59,1.59,0,0,0-1.73-1.26,1.74,1.74,0,0,0-1.68.91.3.3,0,0,1-.29.19h-1.13a.22.22,0,0,1-.24-.24v-6.08a.22.22,0,0,1,.24-.24h5.82a.22.22,0,0,1,.24.24V191a.23.23,0,0,1-.24.24h-4.45c-.05,0-.08,0-.08.08v3.31c0,.08,0,.08.09.05a3,3,0,0,1,1.94-.66,2.92,2.92,0,0,1,3,2.13A6.11,6.11,0,0,1,332,197.61Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M334.05,200.87V190.15a.22.22,0,0,1,.24-.24h3.79c2.23,0,3.65,1.25,3.65,3.2v4.8c0,2-1.42,3.2-3.65,3.2h-3.79A.22.22,0,0,1,334.05,200.87Zm1.61-1.08h2.49a1.94,1.94,0,0,0,2-2.1v-4.36a1.92,1.92,0,0,0-2-2.09h-2.49a.08.08,0,0,0-.08.08v8.39A.09.09,0,0,0,335.66,199.79Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #575756"
        />
        <path
          d="M75.42,9.42v7.46a4,4,0,0,1-.73,2.38,5,5,0,0,1-2.08,1.62,7.81,7.81,0,0,1-3.09.57,7.52,7.52,0,0,1-3-.57,4.92,4.92,0,0,1-2-1.6,4.06,4.06,0,0,1-.72-2.38v-.82a.29.29,0,0,1,.09-.22.25.25,0,0,1,.21-.08H67.6a.28.28,0,0,1,.21.08.29.29,0,0,1,.09.22v.22a1.49,1.49,0,0,0,.46,1.12,1.64,1.64,0,0,0,1.19.46,1.78,1.78,0,0,0,1.24-.46,1.5,1.5,0,0,0,.48-1.14V14.35s0-.08,0-.1-.06,0-.1,0a4.09,4.09,0,0,1-2.5.72,5.44,5.44,0,0,1-2.8-.75,5,5,0,0,1-2-2.15,6.09,6.09,0,0,1-.53-2.68A6.22,6.22,0,0,1,63.8,6.9a5.21,5.21,0,0,1,2.14-2.41,7,7,0,0,1,6.86,0A5.27,5.27,0,0,1,75,7,6.29,6.29,0,0,1,75.42,9.42Zm-4.17-.07A2.65,2.65,0,0,0,70.92,8a1.6,1.6,0,0,0-1.52-.85A1.68,1.68,0,0,0,67.85,8a2.41,2.41,0,0,0-.38,1.38,2.58,2.58,0,0,0,.38,1.43,1.72,1.72,0,0,0,1.5.75,1.69,1.69,0,0,0,1.55-.78A2.59,2.59,0,0,0,71.25,9.35Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M78.87,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.31,7.31,0,0,1,9.08,0,5.67,5.67,0,0,1,1.67,4.31v6.1A5.84,5.84,0,0,1,88,19.94a6.32,6.32,0,0,1-4.55,1.61A6.22,6.22,0,0,1,78.87,19.94Zm6-2.55a2.21,2.21,0,0,0,.56-1.61V9.3a2.21,2.21,0,0,0-.56-1.61A2,2,0,0,0,83.4,7.1a1.94,1.94,0,0,0-1.48.59,2.29,2.29,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61A1.91,1.91,0,0,0,83.4,18,2,2,0,0,0,84.91,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M93.15,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.3,7.3,0,0,1,9.07,0,5.64,5.64,0,0,1,1.68,4.31v6.1a5.81,5.81,0,0,1-1.68,4.39,6.29,6.29,0,0,1-4.55,1.61A6.21,6.21,0,0,1,93.15,19.94Zm6-2.55a2.25,2.25,0,0,0,.56-1.61V9.3a2.25,2.25,0,0,0-.56-1.61,2,2,0,0,0-1.52-.59,1.92,1.92,0,0,0-1.47.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61,1.89,1.89,0,0,0,1.48.58A2,2,0,0,0,99.19,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <g id="bubbles">
          <use
            width="17.72"
            height="17.72"
            transform="translate(46.24 25.77)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(57.83 53.89)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(77.57 50.93)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(46.24 44.55) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(50.23 70.54) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(72.59 37.63) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(60.13 40.88) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(89.84 42.29) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(65.88 32.18) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(45.54 59.86) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(99.1 48.66)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(110.68 76.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(130.43 73.82)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(99.1 67.44) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(103.09 93.43) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(125.45 60.52) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(112.99 63.77) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(142.7 65.18) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(118.74 55.07) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(98.4 82.75) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(46.24 87.23)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(57.83 115.35)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(77.57 112.39)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(46.24 106.01) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(50.23 132) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(72.59 99.09) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(70.44 69.16) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(83.92 131) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(60.13 102.34) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(63.03 80.97) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(46.65 145.67) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(90.84 101.75) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(65.88 93.64) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(74.12 129.04) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(90.78 74.64) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(91.59 65.91) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(85.28 70.15) scale(0.36)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(127.73 89.65) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(147.3 84.36) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(88.01 147.02) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(59.01 146.67) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(45.54 121.32) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(99.1 110.12)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(116.65 98.57)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(79.36 82.58)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(67.44 138.05)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(110.68 138.25)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(130.43 135.28)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(99.1 128.9) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(138.46 111.51) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(125.45 121.98) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(112.99 125.23) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(142.7 126.64) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(118.74 116.53) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(98.4 144.21) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(134.87 92.73)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(165.55 138.25)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(188.29 127.28)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(153.97 130.91) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(193.32 112.52) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(178.31 114.98) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(168.86 125.23) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(196.59 146.12) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(184.05 146.12) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(179.59 132.14) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(149.26 145.21) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(153.51 70.57)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(165.09 98.7)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(184.83 96.73) scale(0.91)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(153.51 89.35) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(157.5 115.35) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(179.85 82.43) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(167.4 85.68) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(197.1 87.09) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(173.14 76.98) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(152.81 104.66) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(207.96 92.42)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(219.54 120.54)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(228.4 137.66)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(207.96 137.66)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(239.29 117.58)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(207.96 111.2) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(234.31 104.28) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(221.85 107.53) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(251.56 108.94) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(227.6 98.83) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(207.26 126.51) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(261.17 113.58)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(275.46 141.7) scale(0.81)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(292.5 138.74)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(248.15 137.76) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(287.52 125.44) scale(0.78)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(275.06 128.69) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(304.77 130.1) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(256.73 128.4) scale(0.39)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(262.47 145.67) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(262.64 132.82) scale(0.62)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(313.37 136.58)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(337.01 141.99) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(330.01 148.02) scale(0.45)"
            xlink:href="#a"
          />
          <use
            width="17.72"
            height="17.72"
            transform="translate(343.5 148.88) scale(0.45)"
            xlink:href="#a"
          />
        </g>
        <path
          d="M146.45,20.9l5.32-13.45a.08.08,0,0,0,0-.1.11.11,0,0,0-.09,0h-3.95a.11.11,0,0,0-.12.12V8.6a.31.31,0,0,1-.3.3h-2.78a.31.31,0,0,1-.3-.3l0-4.55a.29.29,0,0,1,.09-.21.26.26,0,0,1,.21-.09h11.32a.29.29,0,0,1,.22.09.32.32,0,0,1,.08.21V7.1a1.13,1.13,0,0,1-.07.37L150.82,21a.33.33,0,0,1-.35.22H146.7C146.45,21.25,146.36,21.13,146.45,20.9Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M159.07,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.31,7.31,0,0,1,9.08,0,5.67,5.67,0,0,1,1.67,4.31v6.1a5.84,5.84,0,0,1-1.67,4.39,6.32,6.32,0,0,1-4.55,1.61A6.22,6.22,0,0,1,159.07,19.94Zm6-2.55a2.21,2.21,0,0,0,.56-1.61V9.3a2.21,2.21,0,0,0-.56-1.61,2,2,0,0,0-1.51-.59,1.94,1.94,0,0,0-1.48.59,2.29,2.29,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61,1.91,1.91,0,0,0,1.49.58A2,2,0,0,0,165.11,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M173.35,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.3,7.3,0,0,1,9.07,0,5.64,5.64,0,0,1,1.68,4.31v6.1a5.81,5.81,0,0,1-1.68,4.39,6.29,6.29,0,0,1-4.55,1.61A6.21,6.21,0,0,1,173.35,19.94Zm6-2.55a2.25,2.25,0,0,0,.56-1.61V9.3a2.25,2.25,0,0,0-.56-1.61,2,2,0,0,0-1.52-.59,1.92,1.92,0,0,0-1.47.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61,1.89,1.89,0,0,0,1.48.58A2,2,0,0,0,179.39,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M236.91,15.58a6,6,0,0,1-.5,2.52,5.06,5.06,0,0,1-2.12,2.41,6.61,6.61,0,0,1-3.45.89,6.45,6.45,0,0,1-3.43-.9A5.27,5.27,0,0,1,225.26,18a6.16,6.16,0,0,1-.47-2.48V8.12a4,4,0,0,1,.73-2.38,5,5,0,0,1,2.08-1.62,7.81,7.81,0,0,1,3.09-.57,7.69,7.69,0,0,1,3,.56,4.77,4.77,0,0,1,2,1.6,4,4,0,0,1,.72,2.37v.84a.29.29,0,0,1-.09.22.26.26,0,0,1-.21.08h-3.55a.28.28,0,0,1-.21-.08.29.29,0,0,1-.09-.22V8.67a1.49,1.49,0,0,0-.46-1.12,1.65,1.65,0,0,0-1.19-.45,1.73,1.73,0,0,0-1.24.46,1.55,1.55,0,0,0-.48,1.16v1.95c0,.05,0,.09.05.11a.11.11,0,0,0,.1,0,4,4,0,0,1,2.52-.75,5.35,5.35,0,0,1,2.79.75,5.11,5.11,0,0,1,2,2.15A6,6,0,0,1,236.91,15.58Zm-4.15.07a2.59,2.59,0,0,0-.4-1.43,1.72,1.72,0,0,0-1.5-.75,1.67,1.67,0,0,0-1.55.78,2.59,2.59,0,0,0-.35,1.4,2.65,2.65,0,0,0,.33,1.35,1.62,1.62,0,0,0,1.55.85,1.71,1.71,0,0,0,1.55-.85A2.4,2.4,0,0,0,232.76,15.65Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M240.16,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.31,7.31,0,0,1,9.08,0,5.67,5.67,0,0,1,1.67,4.31v6.1a5.84,5.84,0,0,1-1.67,4.39,6.32,6.32,0,0,1-4.55,1.61A6.22,6.22,0,0,1,240.16,19.94Zm6-2.55a2.25,2.25,0,0,0,.56-1.61V9.3a2.25,2.25,0,0,0-.56-1.61,2,2,0,0,0-1.51-.59,1.94,1.94,0,0,0-1.48.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61,1.91,1.91,0,0,0,1.49.58A2,2,0,0,0,246.2,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M254.44,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.3,7.3,0,0,1,9.07,0,5.64,5.64,0,0,1,1.68,4.31v6.1a5.81,5.81,0,0,1-1.68,4.39A6.31,6.31,0,0,1,259,21.55,6.2,6.2,0,0,1,254.44,19.94Zm6-2.55a2.21,2.21,0,0,0,.57-1.61V9.3a2.21,2.21,0,0,0-.57-1.61A2,2,0,0,0,259,7.1a1.91,1.91,0,0,0-1.47.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.33,2.33,0,0,0,.53,1.61A1.93,1.93,0,0,0,259,18,2,2,0,0,0,260.47,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M317.51,15.58a6.46,6.46,0,0,1-.45,2.39,5.21,5.21,0,0,1-2.16,2.49,6.45,6.45,0,0,1-3.49.94,6.53,6.53,0,0,1-3.47-.91A5.32,5.32,0,0,1,305.76,18a7.19,7.19,0,0,1-.4-1.5v-.05c0-.18.1-.28.3-.28h3.65a.39.39,0,0,1,.35.25,1.34,1.34,0,0,0,.18.41,1.57,1.57,0,0,0,1.52,1,1.81,1.81,0,0,0,.95-.26,1.86,1.86,0,0,0,.68-.76,2.63,2.63,0,0,0,.27-1.25,2.6,2.6,0,0,0-.25-1.2,1.43,1.43,0,0,0-.59-.72,1.71,1.71,0,0,0-.93-.26,2.25,2.25,0,0,0-1.08.27,1.43,1.43,0,0,0-.67.71.32.32,0,0,1-.33.24h-3.72a.29.29,0,0,1-.22-.08.32.32,0,0,1-.08-.21V4.05a.32.32,0,0,1,.08-.21.29.29,0,0,1,.22-.09h10.95a.31.31,0,0,1,.3.3V7a.31.31,0,0,1-.3.3h-7a.11.11,0,0,0-.12.12v2.93c0,.05,0,.08,0,.1s.06,0,.1,0A4.6,4.6,0,0,1,312,9.83a5.52,5.52,0,0,1,3.13.89,5.13,5.13,0,0,1,1.95,2.45A7.28,7.28,0,0,1,317.51,15.58Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M320.76,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45a5.71,5.71,0,0,1,1.65-4.33,7.31,7.31,0,0,1,9.08,0,5.67,5.67,0,0,1,1.67,4.31v6.1a5.84,5.84,0,0,1-1.67,4.39,6.32,6.32,0,0,1-4.55,1.61A6.22,6.22,0,0,1,320.76,19.94Zm6-2.55a2.25,2.25,0,0,0,.56-1.61V9.3a2.25,2.25,0,0,0-.56-1.61,2,2,0,0,0-1.51-.59,1.94,1.94,0,0,0-1.48.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.29,2.29,0,0,0,.54,1.61,1.91,1.91,0,0,0,1.49.58A2,2,0,0,0,326.8,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <path
          d="M335,19.94a5.87,5.87,0,0,1-1.65-4.39V9.45A5.71,5.71,0,0,1,335,5.12a7.3,7.3,0,0,1,9.07,0,5.64,5.64,0,0,1,1.68,4.31v6.1a5.81,5.81,0,0,1-1.68,4.39,6.31,6.31,0,0,1-4.55,1.61A6.2,6.2,0,0,1,335,19.94Zm6-2.55a2.21,2.21,0,0,0,.57-1.61V9.3a2.21,2.21,0,0,0-.57-1.61,2,2,0,0,0-1.51-.59,1.91,1.91,0,0,0-1.47.59,2.25,2.25,0,0,0-.55,1.61v6.48a2.33,2.33,0,0,0,.53,1.61,1.93,1.93,0,0,0,1.49.58A2,2,0,0,0,341.07,17.39Z"
          transform="translate(-2.22 -3.16)"
          style="fill: #4d21b5"
        />
        <line
          x1="323.38"
          y1="22.52"
          x2="323.38"
          y2="131.2"
          style="
        fill: none;
        stroke: #4d21b5;
        stroke-linecap: round;
        stroke-miterlimit: 8;
        stroke-dasharray: 0.10000000149011612, 3;
      "
        />
      </svg>
    `;
  }
}

customElements.define("phosphate-decreases", PhosphateDecreasesChart);
